import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDownIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import type { RootState } from '../../store';
import {
  setSelectedDesktopPermissionMode,
  updateSessionPermissionMode,
} from '../../store/slices/coworkSlice';
import { getTargetClient, relayService } from '../../services/relay';
import { i18nService } from '../../services/i18n';
import type { CoworkPermissionMode } from '../../types/cowork';

const MODES: CoworkPermissionMode[] = ['default', 'auto', 'bypassPermissions'];

const LABEL_KEY: Record<CoworkPermissionMode, string> = {
  default: 'coworkPermissionModeDefault',
  auto: 'coworkPermissionModeAuto',
  bypassPermissions: 'coworkPermissionModeBypass',
};

const HINT_KEY: Record<CoworkPermissionMode, string> = {
  default: 'coworkPermissionModeDefaultHint',
  auto: 'coworkPermissionModeAutoHint',
  bypassPermissions: 'coworkPermissionModeBypassHint',
};

interface RelayCoworkPermissionModeSelectorProps {
  /** 新建会话时为实际 clientId；打开已有 Desktop 会话时可能为 __desktop__ 哨兵。 */
  selectedDesktopClientId: string;
}

/**
 * Web Relay 的 Desktop Cowork 权限模式选择器。
 *
 * 新建任务时只维护待创建会话的模式，并随 session.start 下发；已有 Desktop 会话则通过
 * session.setPermissionMode 热更新。所有异步请求都绑定目标 clientId，会话切换后旧请求不会
 * 回写当前 UI，避免跨设备或跨会话污染状态。
 */
const RelayCoworkPermissionModeSelector: React.FC<RelayCoworkPermissionModeSelectorProps> = ({
  selectedDesktopClientId,
}) => {
  const dispatch = useDispatch();
  const currentSession = useSelector((state: RootState) => state.cowork.currentSession);
  const pendingMode = useSelector((state: RootState) => state.cowork.selectedDesktopPermissionMode);
  const [supportedModes, setSupportedModes] = useState<CoworkPermissionMode[]>(MODES);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const existingDesktopSession = currentSession?.origin === 'desktop'
    && !currentSession.id.startsWith('temp-');
  const targetClientId = useMemo(() => {
    if (existingDesktopSession && currentSession) {
      return getTargetClient(currentSession.id);
    }
    return selectedDesktopClientId === '__desktop__' ? null : selectedDesktopClientId;
  }, [currentSession?.id, existingDesktopSession, selectedDesktopClientId]);
  const mode: CoworkPermissionMode = existingDesktopSession
    ? (currentSession?.permissionMode ?? pendingMode)
    : pendingMode;

  useEffect(() => {
    if (!open) return;
    const handleDocumentClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [open]);

  useEffect(() => {
    if (!targetClientId) return;
    let cancelled = false;
    setSupportedModes(MODES);

    const loadConfig = async () => {
      try {
        await relayService.ensureConnected();
        const config = await relayService.getCoworkConfig(targetClientId);
        if (cancelled) return;
        const validModes = config.permissionModes.filter((item) => MODES.includes(item));
        setSupportedModes(validModes.length > 0 ? validModes : MODES);
        // 已有会话以会话自身状态为准；Desktop 全局默认只用于尚未创建的会话。
        if (!existingDesktopSession) {
          dispatch(setSelectedDesktopPermissionMode(config.permissionMode));
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('[RelayCoworkPermissionMode] Failed to load Desktop config:', error);
        }
      }
    };

    void loadConfig();
    return () => {
      cancelled = true;
    };
  }, [dispatch, existingDesktopSession, targetClientId]);

  /** 切换待创建模式，或按会话 ID 热更新已有 Desktop 会话。 */
  const handlePick = async (nextMode: CoworkPermissionMode) => {
    setOpen(false);
    if (nextMode === mode || saving) return;

    if (!existingDesktopSession || !currentSession) {
      dispatch(setSelectedDesktopPermissionMode(nextMode));
      return;
    }
    if (!targetClientId) {
      window.dispatchEvent(new CustomEvent('app:showToast', {
        detail: i18nService.t('relayDesktopOffline'),
      }));
      return;
    }

    const previousMode = mode;
    dispatch(updateSessionPermissionMode({ sessionId: currentSession.id, permissionMode: nextMode }));
    setSaving(true);
    try {
      await relayService.ensureConnected();
      await relayService.setPermissionMode(targetClientId, currentSession.id, nextMode);
    } catch (error) {
      dispatch(updateSessionPermissionMode({
        sessionId: currentSession.id,
        permissionMode: previousMode,
      }));
      window.dispatchEvent(new CustomEvent('app:showToast', {
        detail: `${i18nService.t('relayDesktopOffline')}: ${(error as Error).message}`,
      }));
    } finally {
      setSaving(false);
    }
  };

  const dangerous = mode === 'bypassPermissions';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={saving || !targetClientId}
        title={i18nService.t('coworkPermissionModeLabel')}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          dangerous
            ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
            : 'dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text'
        }`}
      >
        <ShieldCheckIcon className="h-4 w-4 flex-shrink-0" />
        <span className="text-xs whitespace-nowrap">{i18nService.t(LABEL_KEY[mode])}</span>
        <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1.5 z-50 min-w-[250px] rounded-xl py-1 shadow-modal border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface">
          {supportedModes.map((item) => {
            const active = item === mode;
            const danger = item === 'bypassPermissions';
            return (
              <button
                key={item}
                type="button"
                onClick={() => { void handlePick(item); }}
                className="w-full text-left px-3 py-2 transition-colors dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover"
              >
                <div className="flex items-center gap-2">
                  <span className={`text-[13px] ${
                    danger
                      ? 'text-red-600 dark:text-red-400'
                      : active
                        ? 'text-claude-accent'
                        : 'dark:text-claude-darkText text-claude-text'
                  } ${active ? 'font-medium' : ''}`}>
                    {i18nService.t(LABEL_KEY[item])}
                  </span>
                  <span className={`ml-auto text-[10px] font-mono opacity-60 ${
                    danger
                      ? 'text-red-600 dark:text-red-400'
                      : 'dark:text-claude-darkTextSecondary text-claude-textSecondary'
                  }`}>
                    {item}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] leading-snug dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  {i18nService.t(HINT_KEY[item])}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RelayCoworkPermissionModeSelector;
